const DB_NAME = "sumoeats_merchant_drafts";
const DB_VERSION = 1;
const STORE_NAME = "draft_assets";
export const DRAFT_IMAGE_KEY = "deal_local_draft_image";

const hasIndexedDb = () =>
    typeof window !== "undefined" && typeof window.indexedDB !== "undefined";

const openDraftDb = () => {
    if (!hasIndexedDb()) {
        return Promise.resolve(null);
    }

    return new Promise((resolve, reject) => {
        try {
            const request = window.indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME);
                }
            };

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error || new Error("Failed to open draft image database"));
            };
        } catch (error) {
            reject(error);
        }
    });
};

const runStoreRequest = async (mode, execute) => {
    const db = await openDraftDb();
    if (!db) return null;

    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (callback, value) => {
            if (settled) return;
            settled = true;
            try {
                db.close();
            } catch {
                // ignore close failures
            }
            callback(value);
        };

        try {
            const transaction = db.transaction(STORE_NAME, mode);
            const store = transaction.objectStore(STORE_NAME);
            const request = execute(store);

            request.onsuccess = () => {
                finish(resolve, request.result ?? null);
            };

            request.onerror = () => {
                finish(reject, request.error || new Error("Draft image request failed"));
            };

            transaction.onabort = () => {
                finish(reject, transaction.error || new Error("Draft image transaction aborted"));
            };

            transaction.onerror = () => {
                finish(reject, transaction.error || new Error("Draft image transaction failed"));
            };
        } catch (error) {
            finish(reject, error);
        }
    });
};

export const saveDraftImageFile = async (file) => {
    if (!(file instanceof Blob)) return false;

    try {
        await runStoreRequest("readwrite", (store) =>
            store.put(
                {
                    blob: file,
                    name: file.name || "deal-image",
                    type: file.type || "application/octet-stream",
                    updatedAt: Date.now(),
                },
                DRAFT_IMAGE_KEY,
            ),
        );
        return true;
    } catch {
        return false;
    }
};

export const readDraftImageFile = async () => {
    try {
        const record = await runStoreRequest("readonly", (store) =>
            store.get(DRAFT_IMAGE_KEY),
        );

        if (!record || !(record.blob instanceof Blob)) {
            return null;
        }

        const blob = record.blob;
        const name = record.name || "deal-image";
        const type = record.type || blob.type || "application/octet-stream";
        const lastModified = Number(record.updatedAt || Date.now());

        if (typeof File !== "undefined") {
            return new File([blob], name, { type, lastModified });
        }

        return blob;
    } catch {
        return null;
    }
};

export const clearDraftImageFile = async () => {
    try {
        await runStoreRequest("readwrite", (store) => store.delete(DRAFT_IMAGE_KEY));
        return true;
    } catch {
        return false;
    }
};
